/**
 * Footer -- Version display + "Check for updates" button.
 *
 * Shows at the very bottom of the home page, subtle and non-intrusive.
 * Also accessible from Settings > About.
 */

import type { AweniContext } from '../context/AweniContext';
import { updateManager } from '../services/UpdateManager';

const APP_VERSION = '1.1.0';

function escFooter(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ));
}

export function mountFooter(container: HTMLElement, ctx: AweniContext): void {
  const t = ctx.i18n.t.bind(ctx.i18n);

  // Note: a former `opacity: 0.5` on the wrapper composited --text-secondary
  // down to ~#808080 in light / ~#696052 in dark — both ≈ 3.2-3.94 contrast,
  // failing WCAG AA (axe-core flagged it on every page). --text-secondary
  // is already designed as a "dim but readable" tone (5.4:1 cream→slate
  // / 5.7:1 black→sand), so we drop the opacity and rely on the token.
  container.innerHTML = `
    <div style="text-align:center;padding:16px 16px 8px;font-size:12px;color:var(--text-secondary);">
      <span>AWENI&trade; v${APP_VERSION}</span>
      <span style="margin:0 6px;">&middot;</span>
      <button id="aweni-check-update-btn" style="background:none;border:none;cursor:pointer;color:var(--accent);font-size:12px;text-decoration:underline;">
        \u{1F504} ${t('footer_check_updates')}
      </button>
      <span style="margin:0 6px;">&middot;</span>
      <span>&copy; 2026 Learnature-ly</span>
    </div>
    <nav aria-label="${t('footer_legal') || 'Legal'}" style="display:flex;justify-content:center;flex-wrap:wrap;gap:4px 12px;padding:0 16px 16px;font-size:12px;color:var(--text-secondary);">
      <a href="/about/" target="_blank" rel="noopener" style="color:var(--text-secondary);text-decoration:none;">${escFooter(t('footer_about') || 'About')}</a>
      <span aria-hidden="true">&middot;</span>
      <a href="/contact/" target="_blank" rel="noopener" style="color:var(--text-secondary);text-decoration:none;">${escFooter(t('footer_contact') || 'Contact')}</a>
      <span aria-hidden="true">&middot;</span>
      <a href="/privacy/" target="_blank" rel="noopener" style="color:var(--text-secondary);text-decoration:none;">${escFooter(t('footer_privacy') || 'Privacy')}</a>
      <span aria-hidden="true">&middot;</span>
      <a href="/terms/" target="_blank" rel="noopener" style="color:var(--text-secondary);text-decoration:none;">${escFooter(t('footer_terms') || 'Terms')}</a>
      <span aria-hidden="true">&middot;</span>
      <a href="/gdpr/" target="_blank" rel="noopener" style="color:var(--text-secondary);text-decoration:none;">${escFooter(t('footer_gdpr') || 'Your data')}</a>
    </nav>
  `;

  const btn = container.querySelector('#aweni-check-update-btn') as HTMLElement;

  if (btn) {
    btn.addEventListener('click', async () => {
      const original = `\u{1F504} ${t('footer_check_updates')}`;
      btn.textContent = t('common_loading');

      try {
        const manifest = await updateManager.checkForUpdates();

        if (manifest) {
          // Update available \u2014 switch the button into a persistent "tap to update" CTA.
          btn.textContent = t('footer_tap_to_update');
          (btn.style as CSSStyleDeclaration).color = 'var(--accent)';
          btn.onclick = async () => {
            if ('caches' in window) {
              const keys = await caches.keys();
              await Promise.all(keys.map((k) => caches.delete(k)));
            }
            const reg = await navigator.serviceWorker?.getRegistration();
            if (reg) await reg.update();
            window.location.reload();
          };
        } else {
          // Up-to-date \u2014 flash a brief confirmation in the button itself, no
          // permanent second footer line.
          btn.textContent = `\u2713 ${t('settings_up_to_date')}`;
          (btn.style as CSSStyleDeclaration).color = 'var(--accent)';
          setTimeout(() => {
            btn.textContent = original;
            (btn.style as CSSStyleDeclaration).color = 'var(--accent)';
          }, 2200);
        }
      } catch {
        btn.textContent = t('common_error');
        (btn.style as CSSStyleDeclaration).color = '#EF4444';
        setTimeout(() => {
          btn.textContent = original;
          (btn.style as CSSStyleDeclaration).color = 'var(--accent)';
        }, 2200);
      }
    });
  }
}

/**
 * Utility: perform a hard refresh clearing all caches.
 * Can be called from Settings > About > Check for updates.
 */
export async function performHardRefresh(): Promise<void> {
  // Clear all caches
  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
  // Update SW
  const reg = await navigator.serviceWorker?.getRegistration();
  if (reg) await reg.update();
  // Hard refresh
  window.location.reload();
}
